import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Skeleton,
  Typography,
} from "@mui/material";
import Battery50Icon from "@mui/icons-material/Battery50";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import WifiIcon from "@mui/icons-material/Wifi";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import LaunchRoundedIcon from "@mui/icons-material/LaunchRounded";
import VerifiedIcon from "@mui/icons-material/Verified";
import { COLOR_MAP } from "../../../../../constants/ColorNames";
import {
  ICampaignSenderDetails,
  ITemplatePlaceholderValues,
  EMediaType,
} from "../../../../../types/campaign.types";
import { renderMsg } from "../MiddleSection/Message";
import {
  CameraIcon,
  ExcelIcon,
  MicIcon,
  PdfIcon,
  PhoneIcon,
  PlusIcon,
  UploadDocIcon,
  VideoIcon,
} from "../../../../../constants/icons/WhatsAppIcons";
import FounditIcon from "../../../../../constants/icons/FounditIcon";
import { useSelector } from "react-redux";
import { campaignSetupDetailsSelector } from "../../../../../redux/selectors/campaignSetup.selector";

interface IProps {
  message: string;
  mediaFile: File | null;
  mediaType: EMediaType;
  mediaFileName: string;
  mediaFileSize: string;
  senderDetails: ICampaignSenderDetails;
  placeholders: ITemplatePlaceholderValues;
  showMedia: boolean;
}

const iconSize = 20;
export const iconColor = "#17142A";

const RightSection: React.FC<IProps> = ({
  message,
  mediaFile,
  mediaType,
  mediaFileName,
  mediaFileSize,
  senderDetails,
  placeholders,
  showMedia,
}) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [showFullMsg, setShowFullMsg] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const { setUpDetails } = useSelector(campaignSetupDetailsSelector);


  const updatedPlaceholders = useMemo(()=>{

    let placeholdersCopy = {...placeholders};

    const placeholdersCopyKeys = Object.keys(placeholdersCopy);

    // update the placeholders for @min_salary, @max_salary, @notice_period if value is not present in placeholders but present in redux state
    if(placeholdersCopyKeys.includes("@min_salary") && setUpDetails?.salaryRange?.min){
      placeholdersCopy["@min_salary"] = setUpDetails.salaryRange.min;
    }

    if(placeholdersCopyKeys.includes("@max_salary") && setUpDetails?.salaryRange?.max){
      placeholdersCopy["@max_salary"] = setUpDetails.salaryRange.max;
    }

    if(placeholdersCopyKeys.includes("@notice_period") && setUpDetails?.noticePeriod){
      placeholdersCopy["@notice_period"] = setUpDetails.noticePeriod;
    }

    return placeholdersCopy;

  },[placeholders, setUpDetails.noticePeriod, setUpDetails.salaryRange.max, setUpDetails.salaryRange.min])


  const getMsgLimit = useMemo(() => {
    const isImage = !!mediaFile && mediaType === "image";
    const isImageUpdating = isUpdating && mediaType === "image";
    const isDocFile = !!mediaFile && mediaType === "document";
    const isDocUpdating = isUpdating && mediaType === "document";

    if (isImage || isImageUpdating) {
      return 100;
    }
    if (isDocFile || isDocUpdating) {
      return 150;
    }

    return 200;
  }, [mediaFile, mediaType, isUpdating]);

  const handleShowFullMsg = () => {
    setShowFullMsg(true);
  };

  useEffect(() => {
    setIsUpdating(true);
    if (mediaFile) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setTimeout(() => {
          setImageSrc(reader.result as string);
          setIsUpdating(false);
        }, 800);
      };
      reader.readAsDataURL(mediaFile);

      return () => reader.abort();
    } else {
      setTimeout(() => {
        setImageSrc(null);
        setIsUpdating(false);
      }, 1000);
    }
  }, [mediaFile]);

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        py: 2,
        pr: 2,
        boxSizing: "border-box",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          height: 500,
          width: "100%",
          maxWidth: "270px",
          borderRadius: 6,
          borderLeft: "4px solid",
          borderRight: "4px solid",
          borderTop: "4px solid",
          borderBottom: "4px solid",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Notification bar */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 1,
            boxSizing: "border-box",
          }}
        >
          <Typography>
            {new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </Typography>
          <Box>
            <SignalCellularAltIcon sx={{ mr: 0.5 }} />
            <WifiIcon sx={{ mr: 0.5 }} />
            <Battery50Icon sx={{ transform: "rotate(90deg)" }} />
          </Box>
        </Box>

        {/* App Profile Section  */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 1,
            pb: 1,
            boxSizing: "border-box",
          }}
        >
          <Box
            columnGap={1}
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <ArrowBackIosNewOutlinedIcon
              sx={{ fontSize: iconSize - 4, color: iconColor }}
            />
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {/* <Avatar
                sx={{ width: 24, height: 24 }}
                src={`data:image/png;base64,${senderDetails.profilePic}`}
              /> */}
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  backgroundColor: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  p: 0.5,
                }}
              >
                <FounditIcon />
              </Box>

              <Box
                sx={{
                  fontWeight: "fontWeightBold",
                  ml: 1,
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "column",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  {senderDetails.name}{" "}
                  <VerifiedIcon sx={{ color: "#4FB6EC", ml: 0.5 }} />
                </Typography>
                <Typography fontSize={8}>Business Account</Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex" }} gap={1}>
            <VideoIcon />
            <PhoneIcon />
          </Box>
        </Box>
        <Divider />

        {/* Message Section  */}
        <Box
          sx={{
            width: "100%",
            px: 1,
            pb: 1,
            flexGrow: 1,
            boxSizing: "border-box",
            backgroundColor: "#e3dbd3",
            maxHeight: "500px",
            overflowY: "auto",
            backgroundImage:
              "url(https://media.foundit.in/edge/recruiter-outreach-email/campaing/static/whatsAppChatBg.png)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          {!!message && (
            <>
              {" "}
              <Box sx={{ display: "flex", justifyContent: "center", my: 1 }}>
                <Box
                  sx={{
                    backgroundColor: "#cfd1cf",
                    p: 0.5,
                    borderRadius: 1,
                  }}
                >
                  <Typography>Today</Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  p: 1,
                  backgroundColor: COLOR_MAP.WHITE,
                  width: "75%",
                  borderRadius: 2,
                  position: "relative",
                  overflowWrap: "anywhere",
                }}
              >
                {/* Bottom arrow */}
                <Box
                  sx={{
                    position: "absolute",
                    top: -12,
                    left: -17,
                    color: COLOR_MAP.WHITE,
                  }}
                >
                  <ArrowLeftIcon
                    sx={{ fontSize: 32, transform: "rotate(-90deg)" }}
                  />
                </Box>
                {/* Media */}
                {isUpdating && showMedia && (
                  <Skeleton
                    animation="wave"
                    sx={{
                      height: mediaType === "image" ? 150 : 45,
                      width: "100%",
                    }}
                    variant="rectangular"
                  />
                )}
                {mediaType === "image" &&
                  !!imageSrc &&
                  showMedia &&
                  !isUpdating && (
                    <img src={imageSrc} alt="Media" height={150} width="100%" />
                  )}
                {mediaType === "document" &&
                  !!mediaFile &&
                  showMedia &&
                  !isUpdating && (
                    <Box
                      sx={{
                        p: 0.5,
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: COLOR_MAP.GREY_9,
                      }}
                    >
                      {mediaFileName?.toLowerCase()?.includes("pdf") ? (
                        <PdfIcon />
                      ) : (
                        <ExcelIcon />
                      )}
                      <Box sx={{ ml: 1 }}>
                        <Typography variant="h6">{mediaFileName}</Typography>
                        <Box sx={{ display: "flex" }}>
                          <Typography sx={{ fontSize: 10, mr: 1 }}>
                            {mediaFileSize}
                          </Typography>
                          {!!mediaFileName?.split(".")?.[1] && (
                            <Typography
                              sx={{ fontSize: 10, textTransform: "uppercase" }}
                            >
                              {mediaFileName.split(".")?.[1]}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  )}

                {/* Text  */}
                <Box sx={{ my: 1 }}>
                  {showFullMsg ? (
                    renderMsg({
                      content: message,
                      isPreview: true,
                      placeholders: updatedPlaceholders,
                    })
                  ) : (
                    <>
                      {renderMsg({
                        content:
                          message?.length > getMsgLimit
                            ? `${message?.slice(0, getMsgLimit)}...`
                            : message,
                        isPreview: true,
                        placeholders: updatedPlaceholders,
                        handleShowFullMsg,
                      })}
                    </>
                  )}
                </Box>

                {/* CTA  */}
                <Divider sx={{ my: 1 }} />
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Button
                    startIcon={
                      <LaunchRoundedIcon
                        sx={{
                          fontSize: "16px !important",
                          color: COLOR_MAP.WHATS_APP_GREEN,
                        }}
                      />
                    }
                    sx={{
                      p: 0,
                      textTransform: "none",
                      fontSize: 12,
                      color: COLOR_MAP.WHATS_APP_GREEN,
                      cursor: "default",
                    }}
                    disableRipple
                  >
                    Job Apply
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </Box>

        {/* Footer Section */}
        <Divider />
        <Box
          sx={{
            width: "100%",
            display: "flex",
            p: 1,
            boxSizing: "border-box",
            alignItems: "center",
          }}
        >
          <PlusIcon />
          <Box
            sx={{
              border: "1px solid grey",
              borderRadius: 8,
              px: 0.5,
              py: 0.25,
              mx: 1,
              flexGrow: 1,
              display: "flex",
              justifyContent: "end",
              backgroundColor: COLOR_MAP.WHITE,
            }}
          >
            <UploadDocIcon />
          </Box>
          <Box sx={{ mr: 0.5, display: "flex", alignItems: "center" }}>
            <CameraIcon />
          </Box>
          <MicIcon />
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(RightSection);
